// =================================================================================================
// >>> **THE HOOK A SCREEN THAT NEVER UNMOUNTS USES TO READ AGAIN.** <<<
//
// `src/lib/resumeRead.ts` decides; this is the two listeners over it. Split for the reason every law
// in this house is split: the decision must be loadable and RUNNABLE by a FAST gate with no browser
// (R146), and a hook that also decided would be a second answer to *is this read stale* on the one
// class of defect that survives every repair a member is asked to try.
//
// **BOTH EVENTS, ALWAYS, FROM THE ROSTER.** `visibilitychange` is what an installed iOS app fires on
// resume; `focus` is what a desktop tab fires coming back from another window. A surface that
// listened for one of them would be fixed for half the members who reported it — which is the
// daily guidance's own measured note, and the reason the pair is a declared roster rather than two
// lines somebody types.
// =================================================================================================
import { useEffect, useRef } from "react";
import { RESUME_EVENTS, RESUME_RE_READ_MS, shouldReReadOnResume } from "../lib/resumeRead";

/**
 * Call `read` when the app comes back to the foreground and the last read has gone stale.
 *
 * `read` is held in a ref so a caller may pass an inline closure without re-binding the listeners on
 * every render — a listener pair that is torn down and rebuilt sixty times a second is a listener
 * pair that is absent at the moment a resume arrives.
 *
 * **IT NEVER READS ON MOUNT.** The screen's own load effect owns the first read, and a hook that
 * also fired there would race it and double every open (R18: *this has not loaded yet* and *this
 * read is stale* are different facts).
 */
export function useResumeReRead(read: () => void, { afterMs = RESUME_RE_READ_MS, enabled = true } = {}) {
  const fn = useRef(read);
  fn.current = read;
  // `0` means never read, which `shouldReReadOnResume` refuses — so a screen that has not loaded yet
  // cannot be re-read out from under its own mount effect.
  const lastRead = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    // >>> **THE CLOCK IS STARTED HERE, AND THE FIRST DRAFT OF THIS HOOK NEVER FIRED WITHOUT IT.**
    // <<< `lastRead` begins at 0 = NEVER READ, which `shouldReReadOnResume` refuses by design so a
    // resume cannot race a screen's own mount effect. But nothing else wrote it, so the predicate
    // answered false forever and **the hook was a listener pair that could not act** — the exact
    // shape of a capability wired to nothing (R38/R165), inside the repair for a defect whose whole
    // symptom is silence. The screen loads when it mounts; that IS a read, and this records it.
    lastRead.current = Date.now();
    const again = () => {
      if (!shouldReReadOnResume({
        visibility: typeof document === "undefined" ? "hidden" : document.visibilityState,
        lastReadMs: lastRead.current,
        nowMs: Date.now(),
        afterMs,
      })) return;
      lastRead.current = Date.now();
      fn.current();
    };
    for (const e of RESUME_EVENTS) {
      if (e === "focus") window.addEventListener(e, again);
      else document.addEventListener(e, again);
    }
    return () => {
      for (const e of RESUME_EVENTS) {
        if (e === "focus") window.removeEventListener(e, again);
        else document.removeEventListener(e, again);
      }
    };
  }, [afterMs, enabled]);

  /** The screen tells the hook when IT read, so the throttle counts from a real read. */
  return { markRead: () => { lastRead.current = Date.now(); } };
}
